"use client";

import React, {
	ReactNode,
	useState,
	useCallback,
	useEffect,
	useId,
	useRef,
} from "react";
import { createPortal } from "react-dom";
import { motion } from "motion/react";
import { ChevronDownIcon } from "lucide-react";
import { cn } from "../../lib/utils";
import { Button } from "./button";
import {
	PaginationControls,
	type PaginationControlsProps,
} from "./pagination-controls";
import { useElementById } from "../../hooks/useElementById";
import { ToolbarActions } from "./toolbar-actions";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

// ---------------------------------------------------------------------------
// Animation variants (inlined from admin-app)
// ---------------------------------------------------------------------------

const showHide = {
	hidden: { height: 0, opacity: 0, transition: { duration: 0.5 } },
	show: { height: "auto", opacity: 1, transition: { duration: 0.5 } },
};

// ---------------------------------------------------------------------------
// Breakpoint helper.
// `sm`/`lg` align with Tailwind defaults; `md`/`xl` match legacy admin-app widths.
// ---------------------------------------------------------------------------

type TablePageBreakpoint = "sm" | "md" | "lg" | "xl";

// `md` and `xl` use legacy admin-app thresholds so existing table toolbars match prior layout.
const tablePageMinWidthByBreakpoint: Record<TablePageBreakpoint, number> = {
	sm: 640,
	md: 600,
	lg: 1024,
	xl: 1366,
};

// ---------------------------------------------------------------------------
// Root
// ---------------------------------------------------------------------------

type TablePageProps = {
	children: ReactNode;
	className?: string;
	"data-testid"?: string;
};

function TablePageRoot({
	children,
	className,
	"data-testid": testId,
}: TablePageProps) {
	return (
		<div
			className={cn("flex flex-col space-y-5", className)}
			data-testid={testId}
		>
			{children}
		</div>
	);
}

// ---------------------------------------------------------------------------
// Actions (replaces TableActions from admin-app)
// ---------------------------------------------------------------------------

type TablePageActionsProps = {
	search?: ReactNode;
	children?: ReactNode;
	actions?: ReactNode;
	label?: string;
	/** TablePage breakpoint at which layout switches from mobile (collapsed) to desktop (inline). */
	breakpoint?: TablePageBreakpoint;
	className?: string;
	fillFilters?: boolean;
};

function TablePageActions({
	search = null,
	children = null,
	actions = null,
	label = "Filter",
	breakpoint = "lg",
	className,
	fillFilters = false,
}: TablePageActionsProps) {
	const [isOpen, setIsOpen] = useState(false);
	const [isDesktop, setIsDesktop] = useState<boolean | undefined>(undefined);
	const toggle = useCallback(() => setIsOpen((prev) => !prev), []);
	const mobileFiltersId = useId();

	useEffect(() => {
		if (typeof window === "undefined") {
			return;
		}

		const minWidth = tablePageMinWidthByBreakpoint[breakpoint];
		const mediaQuery = window.matchMedia(`(min-width: ${minWidth}px)`);

		const updateIsDesktop = () => {
			setIsDesktop(mediaQuery.matches);
		};

		updateIsDesktop();
		mediaQuery.addEventListener("change", updateIsDesktop);

		return () => {
			mediaQuery.removeEventListener("change", updateIsDesktop);
		};
	}, [breakpoint]);

	return (
		<div className={cn(className)}>
			{isDesktop === undefined ? null : isDesktop ? (
				/* Desktop: inline filter bar */
				<div className="flex flex-wrap items-center justify-between gap-3">
					<div
						className={cn(
							"flex items-center gap-3",
							fillFilters ? "min-w-0 flex-1 flex-nowrap" : "flex-wrap",
						)}
					>
						{search && (
							<div className={cn("w-[260px]", fillFilters && "shrink-0")}>
								{search}
							</div>
						)}
						{children}
					</div>
					{actions && (
						<div className="flex shrink-0 items-center gap-2">{actions}</div>
					)}
				</div>
			) : (
				/* Mobile: collapsible filter toggle */
				<div>
					<Button
						onClick={toggle}
						className="w-full mb-5"
						variant="outline"
						aria-expanded={isOpen}
						aria-controls={mobileFiltersId}
					>
						{label}
						<ChevronDownIcon className="w-4 h-4" />
					</Button>
					<motion.div
						id={mobileFiltersId}
						aria-hidden={!isOpen}
						className="w-full flex flex-col overflow-hidden gap-3 pb-2"
						variants={showHide}
						initial="hidden"
						animate={isOpen ? "show" : "hidden"}
					>
						{search}
						{children}
						{actions && (
							<div className="mb-5 gap-2 items-center flex">{actions}</div>
						)}
					</motion.div>
				</div>
			)}
		</div>
	);
}

type TablePageFilter = {
	key: string;
	node: ReactNode;
	isApplied?: boolean;
};

type TrailingPlacement = "end" | "afterFilters";

type TablePageFilterRowProps = {
	filters: TablePageFilter[];
	onClearAll?: () => void;
	showClearAll?: boolean;
	trailing?: ReactNode;
	trailingPlacement?: TrailingPlacement;
	className?: string;
};

const DEFAULT_FILTER_GAP = 12;

function hasSameKeys(a: ReadonlySet<string>, b: ReadonlySet<string>) {
	return a.size === b.size && Array.from(a).every((key) => b.has(key));
}

function overflowingKeys({
	items,
	gap,
	available,
	moreWidth,
}: {
	items: { key: string; width: number }[];
	gap: number;
	available: number;
	moreWidth: number;
}): Set<string> {
	const total = items.reduce(
		(sum, item, index) => sum + item.width + (index ? gap : 0),
		0,
	);
	if (total <= available) {
		return new Set();
	}
	const budget = available - moreWidth - gap;
	let used = 0;
	let visibleCount = 0;
	for (const item of items) {
		const next = used + (visibleCount ? gap : 0) + item.width;
		if (next > budget) {
			break;
		}
		used = next;
		visibleCount += 1;
	}
	return new Set(items.slice(visibleCount).map((item) => item.key));
}

function useOverflowingFilterKeys(
	filterKeys: string,
	placement: TrailingPlacement,
) {
	const containerRef = useRef<HTMLDivElement>(null);
	const rowRef = useRef<HTMLDivElement>(null);
	const moreMeasureRef = useRef<HTMLSpanElement>(null);
	const trailingRef = useRef<HTMLDivElement>(null);
	const [hiddenKeys, setHiddenKeys] = useState<ReadonlySet<string>>(
		() => new Set(),
	);

	useEffect(() => {
		const container = containerRef.current;
		const row = rowRef.current;
		if (!container || !row || typeof ResizeObserver === "undefined") {
			return;
		}

		const measure = () => {
			const elements = Array.from(
				row.querySelectorAll<HTMLElement>("[data-filter-key]"),
			);
			const parsedGap = Number.parseFloat(getComputedStyle(row).columnGap);
			const gap = Number.isFinite(parsedGap) ? parsedGap : DEFAULT_FILTER_GAP;
			const trailingWidth =
				trailingRef.current?.getBoundingClientRect().width ?? 0;
			const reserved = trailingWidth ? trailingWidth + gap : 0;
			const fit = (available: number) =>
				overflowingKeys({
					items: elements.map((element) => ({
						key: element.dataset.filterKey ?? "",
						width: element.getBoundingClientRect().width,
					})),
					gap,
					available,
					moreWidth: moreMeasureRef.current?.getBoundingClientRect().width ?? 0,
				});
			const inline = fit(container.clientWidth - reserved);
			const next =
				reserved && inline.size === elements.length
					? fit(container.clientWidth)
					: inline;
			setHiddenKeys((previous) =>
				hasSameKeys(previous, next) ? previous : next,
			);
		};

		const observer = new ResizeObserver(measure);
		observer.observe(container);
		if (trailingRef.current) {
			observer.observe(trailingRef.current);
		}
		row
			.querySelectorAll<HTMLElement>("[data-filter-key]")
			.forEach((element) => observer.observe(element));

		return () => observer.disconnect();
	}, [filterKeys, placement]);

	return { containerRef, rowRef, moreMeasureRef, trailingRef, hiddenKeys };
}

function TablePageFilterRow({
	filters,
	onClearAll,
	showClearAll = true,
	trailing = null,
	trailingPlacement = "end",
	className,
}: TablePageFilterRowProps) {
	const { containerRef, rowRef, moreMeasureRef, trailingRef, hiddenKeys } =
		useOverflowingFilterKeys(
			filters.map(({ key }) => key).join("\u0000"),
			trailingPlacement,
		);
	const isAfterFilters = trailingPlacement === "afterFilters";
	const hiddenFilters = filters.filter(({ key }) => hiddenKeys.has(key));
	const isHiddenFilterApplied = hiddenFilters.some(
		({ isApplied }) => isApplied,
	);
	const clearAll =
		onClearAll && showClearAll ? (
			<Button
				variant="ghost"
				size="sm"
				className="shrink-0 px-1 text-neutral-500"
				onClick={onClearAll}
				data-testid="table-filter-row-clear-all"
			>
				Clear all
			</Button>
		) : null;

	return (
		<div
			data-testid="table-filter-row"
			className={cn("flex min-w-0 flex-1 items-center gap-3", className)}
		>
			<div
				ref={containerRef}
				data-testid="table-filter-row-filters"
				className={cn(
					"relative flex min-w-0 flex-1 items-center gap-3",
					isAfterFilters && "flex-wrap",
				)}
			>
				<div
					ref={rowRef}
					className={cn(
						"-m-1 flex min-w-0 flex-nowrap items-center gap-3 overflow-hidden p-1",
						!isAfterFilters && "flex-1",
					)}
				>
					{filters.map(({ key, node }) => {
						const isHidden = hiddenKeys.has(key);
						return (
							<div
								key={key}
								data-filter-key={key}
								data-testid={`table-filter-row-item-${key}`}
								className={cn(
									"shrink-0",
									isHidden && "invisible",
									isHidden && isAfterFilters && "absolute left-0 top-0",
								)}
								aria-hidden={isHidden || undefined}
								inert={isHidden || undefined}
							>
								{node}
							</div>
						);
					})}
				</div>
				<span
					ref={moreMeasureRef}
					aria-hidden="true"
					data-testid="table-filter-row-more-measure"
					className="pointer-events-none invisible absolute left-0 top-0 inline-flex h-8 items-center whitespace-nowrap rounded-[4px] border px-3 text-sm"
				>
					More filters ({filters.length})
				</span>
				{hiddenFilters.length > 0 ? (
					<Popover>
						<PopoverTrigger asChild>
							<Button
								variant={isHiddenFilterApplied ? "filter-active" : "outline"}
								size="sm"
								className="shrink-0"
								data-testid="table-filter-row-more"
							>
								More filters ({hiddenFilters.length})
							</Button>
						</PopoverTrigger>
						<PopoverContent
							align="start"
							className="flex w-auto flex-col items-stretch gap-2 p-2"
						>
							{hiddenFilters.map(({ key, node }) => (
								<div key={key} className="flex flex-col">
									{node}
								</div>
							))}
						</PopoverContent>
					</Popover>
				) : null}
				{isAfterFilters ? (
					<div
						ref={trailingRef}
						data-testid="table-filter-row-trailing"
						className="flex min-w-0 max-w-full shrink-0 flex-wrap items-center gap-3 empty:hidden"
					>
						{trailing}
						{clearAll}
					</div>
				) : null}
			</div>
			{isAfterFilters ? null : (
				<>
					{clearAll}
					{trailing ? <div className="shrink-0">{trailing}</div> : null}
				</>
			)}
		</div>
	);
}

// ---------------------------------------------------------------------------
// Content (bordered table wrapper)
// ---------------------------------------------------------------------------

type TablePageContentProps = {
	children: ReactNode;
	className?: string;
	hasBorder?: boolean;
};

function TablePageContent({
	children,
	className,
	hasBorder = true,
}: TablePageContentProps) {
	return (
		<div
			className={cn(
				"border border-muted overflow-x-auto rounded-lg",
				!hasBorder && "border-none",
				className,
			)}
		>
			{children}
		</div>
	);
}

// ---------------------------------------------------------------------------
// Pagination (wraps PaginationControls)
// ---------------------------------------------------------------------------

type TablePagePaginationProps = PaginationControlsProps & {
	showIntercomSpacer?: boolean;
};

function IntercomPlaceholderSpacer() {
	const target = useElementById("intercom-placeholder");

	if (!target) {
		return null;
	}

	return createPortal(<div className="h-[60px]" />, target);
}

function TablePagePagination({
	showIntercomSpacer = false,
	...props
}: TablePagePaginationProps) {
	return (
		<>
			<PaginationControls {...props} />
			{showIntercomSpacer ? <IntercomPlaceholderSpacer /> : null}
		</>
	);
}

// ---------------------------------------------------------------------------
// Compound export
// ---------------------------------------------------------------------------

export const TablePage = Object.assign(TablePageRoot, {
	Actions: TablePageActions,
	FilterRow: TablePageFilterRow,
	Content: TablePageContent,
	Pagination: TablePagePagination,
	/** The described action row for the toolbar's `actions` slot. Also exported standalone. */
	ToolbarActions,
});

export type {
	TablePageProps,
	TablePageActionsProps,
	TablePageFilter,
	TablePageFilterRowProps,
	TablePageContentProps,
	TablePagePaginationProps,
};
